import { useState, useEffect, useCallback } from 'react'
import { useStore } from '../store/useStore'
import { P5_VERSIONS } from '../config/p5versions'
import { 
  FileText, 
  Code, 
  Eye, 
  Sparkles, 
  MoreHorizontal,
  FolderOpen,
  RefreshCw,
  Download,
  Play,
  Square,
  LogIn,
  Info,
  Bug,
  LayoutTemplate,
  Smartphone,
  Vibrate,
  BookOpen,
  History
} from 'lucide-react'
import FileTree from './FileTree'
import Editor from './Editor'
import Preview from './Preview'
import AiAssistantSimple from './AiAssistantSimple'
import EnhancedDebugConsole from './EnhancedDebugConsole'
import SketchBrowser from './SketchBrowser'
import TemplateModal from './TemplateModal'
import { downloadSketch } from '../utils/downloadSketch'
import { haptic } from '../utils/haptics'
import { useShakeDetector, supportsShakeDetection } from '../hooks/useShakeDetector'
import { showToast } from './ui/Toast'

export default function MobileLayout({ onAuthClick, onAboutClick, onChangelogClick, isTablet = false }) {
  const { 
    showConsole, 
    isAuthenticated,
    sketchName,
    setSketchName,
    p5Version,
    setP5Version,
    files,
    setTurboMode
  } = useStore()
  const [activeTab, setActiveTab] = useState(() => {
    // Randomly start in AI or Preview panel on mobile
    return Math.random() < 0.5 ? 'ai' : 'preview'
  })
  
  // Enable turbo mode by default on mobile
  useEffect(() => {
    setTurboMode(true)
  }, [])
  const [showBrowser, setShowBrowser] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [shakeEnabled, setShakeEnabled] = useState(false)

  // Sync isRunning state with actual sketch execution
  useEffect(() => {
    const handleSketchStarted = () => setIsRunning(true)
    const handleSketchStopped = () => setIsRunning(false)
    
    window.addEventListener('sketchStarted', handleSketchStarted)
    window.addEventListener('sketchStopped', handleSketchStopped)
    
    return () => {
      window.removeEventListener('sketchStarted', handleSketchStarted)
      window.removeEventListener('sketchStopped', handleSketchStopped)
    }
  }, [])

  // Shake to randomize feature
  const handleShake = useCallback(() => {
    haptic('heavy')
    showToast('🎲 Shake detected! Ask AI to remix your code', 'info')
    // Could auto-trigger AI with "remix this sketch" prompt
    setActiveTab('ai')
  }, [])

  const { requestPermission } = useShakeDetector(handleShake, { enabled: shakeEnabled })

  const enableShake = async () => {
    const granted = await requestPermission()
    if (granted) {
      setShakeEnabled(true)
      haptic('success')
      showToast('Shake detection enabled! Shake to remix', 'success')
    } else {
      showToast('Motion permission denied', 'error')
    }
  }

  const handleRun = () => {
    haptic('medium')
    window.dispatchEvent(new CustomEvent('runSketch'))
    setIsRunning(true)
  }

  const handleStop = () => {
    haptic('light')
    window.dispatchEvent(new CustomEvent('stopSketch'))
    setIsRunning(false)
  }

  const handleSync = () => {
    haptic('light')
    window.dispatchEvent(new CustomEvent('syncSketch'))
  }

  const handleDownload = () => {
    haptic('success')
    downloadSketch(sketchName, files)
    showToast('Sketch downloaded', 'success')
  }
  
  const p5Versions = P5_VERSIONS

  // Tabs - include Console for easy access on mobile/tablet
  const tabs = [
    { id: 'files', label: 'Files', icon: FileText },
    { id: 'editor', label: 'Code', icon: Code },
    { id: 'preview', label: 'Preview', icon: Eye },
    { id: 'console', label: 'Console', icon: Bug },
    { id: 'ai', label: 'AI', icon: Sparkles },
    { id: 'more', label: 'More', icon: MoreHorizontal }
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Content Area */}
      <div className="flex-1 overflow-hidden bg-ide-bg">
        {activeTab === 'files' && (
          <div className="h-full p-1.5">
            <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden">
              <FileTree />
            </div>
          </div>
        )}
        
        {activeTab === 'editor' && (
          <div className="h-full p-1.5">
            <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden">
              <Editor />
            </div>
          </div>
        )}
        
        {activeTab === 'preview' && (
          <div className="h-full p-1.5">
            <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden">
              <Preview />
            </div>
          </div>
        )}
        
        {activeTab === 'ai' && (
          <div className="h-full p-1.5">
            <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden">
              <AiAssistantSimple />
            </div>
          </div>
        )}

        {activeTab === 'console' && (
          <div className="h-full p-1.5">
            <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden">
              <EnhancedDebugConsole />
            </div>
          </div>
        )}
        
        {activeTab === 'more' && (
          <div className="h-full p-3 overflow-y-auto">
            <div className="max-w-md mx-auto space-y-3">
              <h2 className="text-lg font-semibold text-ide-text mb-4">Settings</h2>
              
              {/* New from Template - Prominent */}
              <button
                type="button"
                onClick={() => {
                  haptic('light')
                  setShowTemplates(true)
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-ide-accent/10 border border-ide-accent/30 rounded-lg transition-all active:scale-[0.98] text-ide-accent mb-4"
              >
                <LayoutTemplate size={20} />
                <span className="font-medium">New from Template</span>
              </button>
              
              {/* Sketch Name */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ide-textMuted">Sketch Name</label>
                <input
                  type="text"
                  value={sketchName}
                  onChange={(e) => setSketchName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-ide-surface border border-ide-border rounded-lg text-ide-text focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30"
                  placeholder="Enter sketch name..."
                />
              </div>

              {/* P5.js Version */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ide-textMuted">p5.js Version</label>
                <select
                  value={p5Version}
                  onChange={(e) => setP5Version(e.target.value)}
                  className="w-full px-3 py-2.5 bg-ide-surface border border-ide-border rounded-lg text-ide-text focus:outline-none focus:border-ide-borderFocus"
                >
                  {p5Versions.map(version => (
                    <option key={version} value={version}>v{version}</option>
                  ))}
                </select>
              </div>

              {/* Run/Stop */}
              <button
                type="button"
                onClick={isRunning ? handleStop : handleRun}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all active:scale-[0.98] ${
                  isRunning
                    ? 'bg-ide-error hover:bg-red-600 text-white'
                    : 'bg-ide-accent hover:bg-ide-accentHover text-white'
                }`}
              >
                {isRunning ? <Square size={20} fill="white" /> : <Play size={20} fill="white" />}
                <span className="font-medium">{isRunning ? 'Stop Sketch' : 'Run Sketch'}</span>
              </button>

              {/* Browse Sketches */}
              <button
                type="button"
                onClick={() => setShowBrowser(true)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all active:scale-[0.98] text-ide-text"
              >
                <FolderOpen size={20} className="text-ide-textMuted" />
                <span className="font-medium">Browse Sketches</span>
              </button>

              {/* Sync */}
              <button
                type="button"
                onClick={handleSync}
                disabled={!isAuthenticated}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                  isAuthenticated
                    ? 'bg-ide-surface hover:bg-ide-surfaceHover border-ide-border text-ide-text active:scale-[0.98]'
                    : 'bg-ide-surface border-ide-border text-ide-textDim cursor-not-allowed'
                }`}
              >
                <RefreshCw size={20} className="text-ide-textMuted" />
                <span className="font-medium">Sync to P5 Editor</span>
                {!isAuthenticated && <span className="ml-auto text-xs text-ide-textDim">Login required</span>}
              </button>

              {/* Download */}
              <button
                type="button"
                onClick={handleDownload}
                className="w-full flex items-center gap-3 px-4 py-3 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all active:scale-[0.98] text-ide-text"
              >
                <Download size={20} className="text-ide-textMuted" />
                <span className="font-medium">Download Sketch</span>
              </button>

              <div className="border-t border-ide-border pt-4 mt-4">
                <h3 className="text-xs font-semibold text-ide-textDim uppercase tracking-wider mb-3">Account & Info</h3>
                
                {/* Login/Account */}
                {!isAuthenticated && (
                  <button
                    type="button"
                    onClick={onAuthClick}
                    className="w-full flex items-center gap-3 px-4 py-2.5 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all active:scale-[0.98] text-ide-text mb-2"
                  >
                    <LogIn size={18} className="text-ide-textMuted" />
                    <span>Login / Sign Up</span>
                  </button>
                )}

                {/* About */}
                <a
                  href="/about"
                  onClick={(e) => {
                    e.preventDefault()
                    onAboutClick()
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all active:scale-[0.98] text-ide-text mb-2"
                >
                  <Info size={18} className="text-ide-textMuted" />
                  <span>About</span>
                </a>

                {/* Changelog */}
                <a
                  href="/changelog"
                  onClick={(e) => {
                    e.preventDefault()
                    onChangelogClick()
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all active:scale-[0.98] text-ide-text mb-2"
                >
                  <History size={18} className="text-ide-textMuted" />
                  <span>Changelog</span>
                </a>

                {/* Research */}
                <a
                  href="/research"
                  className="w-full flex items-center gap-3 px-4 py-2.5 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all active:scale-[0.98] text-ide-text mb-2"
                >
                  <BookOpen size={18} className="text-ide-textMuted" />
                  <span>Research</span>
                </a>

                {/* User Guide */}
                <a
                  href="/docs"
                  className="w-full flex items-center gap-3 px-4 py-2.5 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all active:scale-[0.98] text-ide-text"
                >
                  <BookOpen size={18} className="text-ide-textMuted" />
                  <span>User Guide</span>
                </a>
              </div>

              {/* Experimental Features */}
              {supportsShakeDetection() && (
                <div className="border-t border-ide-border pt-4 mt-4">
                  <h3 className="text-xs font-semibold text-ide-textDim uppercase tracking-wider mb-3">Experimental</h3>
                  
                  <button
                    type="button"
                    onClick={enableShake}
                    disabled={shakeEnabled}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all active:scale-[0.98] ${
                      shakeEnabled 
                        ? 'bg-ide-success/10 border-ide-success/30 text-ide-success'
                        : 'bg-ide-surface hover:bg-ide-surfaceHover border-ide-border text-ide-text'
                    }`}
                  >
                    <Vibrate size={18} className={shakeEnabled ? 'text-ide-success' : 'text-ide-textMuted'} />
                    <span>{shakeEnabled ? 'Shake to Remix: ON' : 'Enable Shake to Remix'}</span>
                  </button>
                  <p className="text-xs text-ide-textDim mt-2 px-1">
                    Shake your device to trigger AI remix suggestions
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Tab Navigation - Optimized for touch on phones and tablets */}
      <nav 
        className="bg-ide-surface border-t border-ide-border flex-shrink-0" 
        style={{ 
          // Use max() to ensure minimum padding for Android gesture nav + iOS safe area
          paddingBottom: 'max(8px, env(safe-area-inset-bottom, 8px))'
        }}
      >
        <div className={`flex justify-around items-center px-1 ${isTablet ? 'py-2' : 'py-1.5'}`}>
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex flex-col items-center gap-0.5 rounded-lg transition-all ${
                isTablet ? 'px-3 py-2 min-w-[64px]' : 'px-2 py-1.5 min-w-[48px]'
              } ${
                activeTab === id
                  ? 'text-ide-accent bg-ide-accentMuted'
                  : 'text-ide-textMuted hover:text-ide-text active:bg-ide-surfaceHover'
              }`}
            >
              <Icon size={isTablet ? 22 : 18} strokeWidth={activeTab === id ? 2.5 : 2} />
              <span className={`font-medium ${isTablet ? 'text-xs' : 'text-[10px]'}`}>{label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Sketch Browser Modal */}
      {showBrowser && (
        <SketchBrowser onClose={() => setShowBrowser(false)} />
      )}
      
      {/* Template Modal */}
      {showTemplates && (
        <TemplateModal onClose={() => setShowTemplates(false)} />
      )}
    </div>
  )
}
